import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePublishToEmma } from '../hooks/usePublishToEmma';
import portalT from '../translations/portal';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import {
  ArrowLeft, Send, Loader, BookOpen, Trophy, Lightbulb,
  Banknote, Warehouse, Megaphone, Monitor, Settings, TrendingUp,
  Users, Wrench, Briefcase, GraduationCap, MessageSquare, Plus, ChevronRight,
} from 'lucide-react';
import ExportPDFButton from '../components/ExportPDFButton';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const ROLE_ICONS = {
  finance: Banknote, warehouse: Warehouse, marketing: Megaphone, it: Monitor,
  operations: Settings, sales: TrendingUp, hr: Users, engineering: Wrench,
  management: Briefcase, training: GraduationCap,
};

const AICoach = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { lang } = useLanguage();
  const t = portalT[lang];
  const tp = t.toolPages.coach;
  const chatEndRef = useRef(null);

  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(null);
  const [activeTab, setActiveTab] = useState('chat');

  // Chat state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  
  const lastAssistant = (Array.isArray(messages) ? messages : []).filter(m => m.role === 'assistant').slice(-1)[0]?.content;
  usePublishToEmma('AI Coach', lastAssistant);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { init(); }, []);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const init = async () => {
    try {
      const [rolesRes, roleRes] = await Promise.all([
        axios.get(`${API}/compass/roles`),
        axios.get(`${API}/compass/my-role`, { headers }),
      ]);
      setRoles(rolesRes.data.roles);
      if (roleRes.data.role) {
        setSelectedRole(roleRes.data.role);
        await loadContent(roleRes.data.role);
      }
    } catch (e) {
      console.error('Init error:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadContent = async (role) => {
    try {
      const res = await axios.get(`${API}/compass/coach/content`, { headers });
      setContent(res.data);
    } catch (e) {
      console.error('Content error:', e);
    }
  };

  const handleRoleSelect = async (roleId) => {
    setLoading(true);
    try {
      await axios.post(`${API}/compass/set-role`, { role: roleId }, { headers });
      setSelectedRole(roleId);
      setMessages([]);
      setSessionId(null);
      await loadContent(roleId);
    } catch (e) {
      console.error('Role error:', e);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (text) => {
    if (!((text) == null ? '' : String(text)).trim() || sending) return;
    const userMsg = { role: 'user', content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/compass/coach/chat`,
        { message: text, session_id: sessionId, language: lang },
        { headers }
      );
      setSessionId(res.data.session_id);
      setMessages((prev) => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (e) {
      setMessages((prev) => [...prev, { role: 'assistant', content: lang === 'nl' ? 'Sorry, er is iets misgegaan. Probeer het opnieuw.' : 'Sorry, something went wrong. Please try again.' }]);
    } finally {
      setSending(false);
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setSessionId(null);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loader"></div>
        <p>{t.common.loading}</p>
      </div>
    );
  }

  // ─── Role Selection ───
  if (!selectedRole) {
    return (
      <div className="tool-page">
        <div className="tool-header">
          <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary">
            <ArrowLeft size={16} /> {t.common.back}
          </button>
          <h1 className="heading-1">{tp.title} — {tp.selectRole}</h1>
        </div>
        <div className="tool-content">
          <div className="compass-role-intro">
            <p className="body-large text-secondary" style={{ textAlign: 'center', marginBottom: '2rem' }}>
              {lang === 'nl' ? 'Selecteer je rol zodat de AI Coach je gepersonaliseerde begeleiding, lessen en uitdagingen kan geven.' : 'Select your role so the AI Coach can give you personalized guidance, lessons, and challenges.'}
            </p>
          </div>
          <div className="compass-role-grid" data-testid="role-grid">
            {(Array.isArray(roles) ? roles : []).map((r) => {
              const Icon = ROLE_ICONS[r.id] || Briefcase;
              return (
                <button
                  key={r.id}
                  className="compass-role-card"
                  onClick={() => handleRoleSelect(r.id)}
                  data-testid={`role-${r.id}`}
                >
                  <div className="compass-role-icon"><Icon size={24} /></div>
                  <span className="body-medium">{r.label}</span>
                  <ChevronRight size={16} className="compass-role-arrow" />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ─── Main Coach View ───
  const RoleIcon = ROLE_ICONS[selectedRole] || Briefcase;
  const roleLabel = (Array.isArray(roles) ? roles : []).find((r) => r.id === selectedRole)?.label || selectedRole;

  return (
    <div className="compass-coach-page" data-testid="coach-page">
      {/* Sidebar */}
      <aside className="compass-sidebar">
        <div className="compass-sidebar-header">
          <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary" style={{ width: '100%', justifyContent: 'center' }}>
            <ArrowLeft size={14} /> {t.common.aiTools}
          </button>
        </div>

        <div className="compass-role-badge" data-testid="current-role">
          <RoleIcon size={16} />
          <span className="body-small">{roleLabel}</span>
          <button className="compass-role-change" onClick={() => { setSelectedRole(null); setContent(null); }}>
            {lang === 'nl' ? 'Wijzigen' : 'Change'}
          </button>
        </div>

        <nav className="compass-sidebar-nav">
          <button className={`compass-nav-item ${activeTab === 'chat' ? 'active' : ''}`} onClick={() => setActiveTab('chat')} data-testid="tab-chat">
            <MessageSquare size={16} /> {lang === 'nl' ? 'Chat' : 'Chat'}
          </button>
          <button className={`compass-nav-item ${activeTab === 'lessons' ? 'active' : ''}`} onClick={() => setActiveTab('lessons')} data-testid="tab-lessons">
            <BookOpen size={16} /> {tp.microLessons}
          </button>
          <button className={`compass-nav-item ${activeTab === 'challenges' ? 'active' : ''}`} onClick={() => setActiveTab('challenges')} data-testid="tab-challenges">
            <Trophy size={16} /> {tp.challenges}
          </button>
        </nav>

        {activeTab === 'chat' && (
          <button className="compass-new-chat" onClick={startNewChat} data-testid="new-chat-btn">
            <Plus size={14} /> {tp.newSession}
          </button>
        )}
        {activeTab === 'chat' && messages.length > 0 && (
          <ExportPDFButton
            title={`AI Coach — ${roleLabel}`}
            content={(Array.isArray(messages) ? messages : []).map(m => `**${m.role === 'user' ? 'User' : 'AI Coach'}:**\n${m.content}`).join('\n\n')}
            toolName="AI Coach"
            className="compass-new-chat"
          />
        )}
      </aside>

      {/* Main Content */}
      <main className="compass-main">
        {activeTab === 'chat' && (
          <div className="compass-chat-container">
            <div className="compass-chat-messages" data-testid="chat-messages">
              {messages.length === 0 && (
                <div className="compass-chat-welcome">
                  <RoleIcon size={40} />
                  <h2 className="heading-2">{lang === 'nl' ? `Hallo! Ik ben je ${roleLabel} AI Coach` : `Hi! I'm your ${roleLabel} AI Coach`}</h2>
                  <p className="body-medium text-secondary">
                    {lang === 'nl' ? `Vraag me alles over het toepassen van AI in je ${((roleLabel) == null ? '' : String(roleLabel)).toLowerCase()} rol. Ik geef je praktische tips, oefeningen en begeleiding.` : `Ask me anything about applying AI in your ${((roleLabel) == null ? '' : String(roleLabel)).toLowerCase()} role. I'll give you practical tips, exercises, and guidance.`}
                  </p>
                  {content?.suggestions && (
                    <div className="compass-suggestions" data-testid="suggestions">
                      {(Array.isArray(content.suggestions) ? content.suggestions : []).map((s, i) => (
                        <button key={i} className="compass-suggestion-btn" onClick={() => sendMessage(s)} data-testid={`suggestion-${i}`}>
                          <Lightbulb size={14} /> {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {(Array.isArray(messages) ? messages : []).map((msg, i) => (
                <div key={i} className={`compass-msg compass-msg-${msg.role}`} data-testid={`msg-${i}`}>
                  {msg.role === 'assistant' ? (
                    <div className="compass-msg-content markdown-content">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                  ) : (
                    <div className="compass-msg-content">{msg.content}</div>
                  )}
                </div>
              ))}

              {sending && (
                <div className="compass-msg compass-msg-assistant">
                  <div className="compass-msg-content compass-typing">
                    <Loader className="spin" size={16} /> {lang === 'nl' ? 'Denken...' : 'Thinking...'}
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            <form className="compass-chat-input" onSubmit={(e) => { e.preventDefault(); sendMessage(input); }} data-testid="chat-form">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={tp.chatPlaceholder}
                disabled={sending}
                className="form-input"
                data-testid="chat-input"
              />
              <button type="submit" disabled={sending || !((input) == null ? '' : String(input)).trim()} className="btn-primary compass-send-btn" data-testid="send-btn">
                <Send size={16} />
              </button>
            </form>
          </div>
        )}

        {activeTab === 'lessons' && content?.lessons && (
          <div className="compass-lessons" data-testid="lessons-panel">
            <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>{tp.microLessons} — {roleLabel}</h2>
            <div className="compass-lesson-grid">
              {(Array.isArray(content.lessons) ? content.lessons : []).map((l, i) => (
                <div key={i} className="compass-lesson-card" data-testid={`lesson-${i}`}>
                  <div className="compass-lesson-header">
                    <BookOpen size={18} />
                    <span className="compass-lesson-duration">{l.duration}</span>
                  </div>
                  <h3 className="heading-3">{l.title}</h3>
                  <p className="body-small text-secondary">{l.desc}</p>
                  <button className="btn-primary" style={{ marginTop: 'auto' }}
                    onClick={() => { setActiveTab('chat'); sendMessage(lang === 'nl' ? `Start de microles: "${l.title}" — ${l.desc}. Begeleid me stap voor stap.` : `Start the micro-lesson: "${l.title}" — ${l.desc}. Guide me step by step.`); }}>
                    {lang === 'nl' ? 'Start Les' : 'Start Lesson'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'challenges' && content?.challenges && (
          <div className="compass-challenges" data-testid="challenges-panel">
            <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>{tp.challenges}</h2>
            <div className="compass-lesson-grid">
              {(Array.isArray(content.challenges) ? content.challenges : []).map((c, i) => (
                <div key={i} className="compass-lesson-card" data-testid={`challenge-${i}`}>
                  <div className="compass-lesson-header">
                    <Trophy size={18} />
                    <span className={`compass-difficulty compass-diff-${c.difficulty}`}>{c.difficulty}</span>
                  </div>
                  <h3 className="heading-3">{c.title}</h3>
                  <p className="body-small text-secondary">{c.desc}</p>
                  <button className="btn-primary" style={{ marginTop: 'auto' }}
                    onClick={() => { setActiveTab('chat'); sendMessage(lang === 'nl' ? `Ik wil de uitdaging proberen: "${c.title}" — ${c.desc}. Begeleid me stap voor stap.` : `I want to try the challenge: "${c.title}" — ${c.desc}. Guide me through it step by step.`); }}>
                    {lang === 'nl' ? 'Start Uitdaging' : 'Start Challenge'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AICoach;
